import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "../../mocks/categories";

const categoryShortLabels: Record<string, string> = {
  rpg: "RPG",
  sandbox: "Sandbox",
  action: "Action",
  entertainment: "Fun",
  movies: "Movies",
  anime: "Anime",
  love: "Love",
  animals: "Animals",
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const bgClass =
    !isHome || scrolled
      ? "bg-white shadow-sm border-b border-gray-100"
      : "bg-transparent";

  const textClass = !isHome || scrolled ? "text-gray-700" : "text-white";
  const logoTextClass = !isHome || scrolled ? "text-gray-900" : "text-white";
  const hoverClass =
    !isHome || scrolled ? "hover:bg-gray-100" : "hover:bg-white/20";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass}`}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 cursor-pointer flex-shrink-0">
          <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53]">
            <i className="ri-gamepad-fill text-white text-sm" />
          </div>
          <span
            className={`font-extrabold text-xl tracking-tight transition-colors ${logoTextClass}`}
            style={{ fontFamily: "'Nunito', sans-serif" }}
          >
            Funs<span style={{ color: !isHome || scrolled ? "#FF6B6B" : "#FFE066" }}>Test</span>
          </span>
        </Link>

        {/* Center Nav — individual category links */}
        <div className="hidden lg:flex items-center gap-0.5 flex-1 justify-center">
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/categories?cat=${cat.slug}`}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${textClass} ${hoverClass}`}
            >
              <span
                className={`w-5 h-5 flex items-center justify-center rounded-full bg-gradient-to-br ${cat.color}`}
              >
                <i className={`${cat.icon} text-white`} style={{ fontSize: "10px" }} />
              </span>
              {categoryShortLabels[cat.id] ?? cat.label}
            </Link>
          ))}
        </div>

        {/* Right Side */}
        <div className="hidden lg:flex items-center gap-2 flex-shrink-0">
          <Link
            to="/write-for-us"
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${hoverClass} cursor-pointer whitespace-nowrap ${textClass}`}
          >
            Write for Us
          </Link>
          <Link
            to="/categories"
            className="px-5 py-2 bg-[#FF6B6B] hover:bg-[#ff5252] text-white text-sm font-bold rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Start a Quiz ✨
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          className={`lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer ${textClass}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`text-xl ${menuOpen ? "ri-close-line" : "ri-menu-line"}`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-4">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Categories</p>
            <div className="grid grid-cols-2 gap-2 mb-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/categories?cat=${cat.slug}`}
                  className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                  onClick={() => setMenuOpen(false)}
                >
                  <span
                    className={`w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-br ${cat.color} flex-shrink-0`}
                  >
                    <i className={`${cat.icon} text-white text-xs`} />
                  </span>
                  <span className="text-sm font-medium text-gray-700 leading-tight">
                    {categoryShortLabels[cat.id] ?? cat.label}
                  </span>
                </Link>
              ))}
            </div>
            <div className="border-t border-gray-100 pt-3 space-y-1">
              <Link
                to="/write-for-us"
                className="block py-2.5 text-sm font-semibold text-gray-700"
                onClick={() => setMenuOpen(false)}
              >
                <i className="ri-quill-pen-line mr-2" /> Write for Us
              </Link>
              <Link
                to="/categories"
                className="block w-full py-2.5 bg-[#FF6B6B] text-white text-sm font-bold rounded-full text-center mt-2"
                onClick={() => setMenuOpen(false)}
              >
                Start a Quiz ✨
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
